import React, { useEffect, useState } from "react";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { LayoutGrid, Table } from "lucide-react";

export type ViewMode = "card" | "list";

interface ViewToggleProps {
  storageKey?: string;
  onChange?: (viewMode: ViewMode) => void;
  defaultView?: ViewMode;
}

const ViewToggle: React.FC<ViewToggleProps> = ({
  storageKey = "rosterly-view-mode",
  onChange,
  defaultView = "card",
}) => {
  // Initialize state from localStorage or use default
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    if (typeof window !== "undefined") {
      const savedView = localStorage.getItem(storageKey);
      return (savedView as ViewMode) || defaultView;
    }
    return defaultView;
  });

  // Update localStorage and trigger onChange when viewMode changes
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(storageKey, viewMode);
    }
    if (onChange) {
      onChange(viewMode);
    }
  }, [viewMode, storageKey, onChange]);

  return (
    <ToggleGroup
      type="single"
      value={viewMode}
      onValueChange={(value) => {
        if (value) setViewMode(value as ViewMode);
      }}
      className="border rounded-md"
    >
      <ToggleGroupItem value="card" aria-label="Card View" title="Card View">
        <LayoutGrid className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline">Card</span>
      </ToggleGroupItem>
      <ToggleGroupItem value="list" aria-label="List View" title="Table View">
        <Table className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline">List</span>
      </ToggleGroupItem>
    </ToggleGroup>
  );
};

export default ViewToggle;
